import { BufferAttribute, Group, IcosahedronGeometry, Matrix4, Mesh, MeshStandardMaterial, Vector3 } from 'three'
import { BufferGeometryUtils } from 'three/examples/jsm/Addons'
import type { DrillDataSet, DrillHoleApiData, DrillHoles } from '~/entities/drillHole'
import type { ApiListView } from '~/entities/api'
import type { DrillDataLocalization } from '~/entities/drillLabel'
import { disposeScene } from '~/lib/three-d-viewer/utils'

export class DrillHoleManager {
  public group = new Group()

  private holes: Map<number, { data: DrillHoleApiData[]; config: {}; localization?: DrillDataLocalization }> = new Map()
  private collarMesh: Mesh | null = null
  private loaderConfig: { uuid: string | null }
  private modelId: number | null = null

  constructor(loaderConfig: { uuid: string | null }) {
    this.loaderConfig = loaderConfig
  }

  async fetchHoles({ modelId, drillDataSets }: { modelId: number; drillDataSets: DrillDataSet[] }) {
    this.modelId = modelId

    if (this.modelId !== modelId) return // avoid race condition

    this.addHolesForDataSet(drillDataSets)
    this.createAllDrillHoles()
  }

  async addHolesForDataSet(drillDataSets: DrillDataSet[]) {
    this.holes = await this.getDrillHoleMap(drillDataSets)
  }

  getHolesForDrillDataSet(drillDataSetId: number): DrillHoleApiData[] {
    return this.holes.get(drillDataSetId)?.data || []
  }

  private createAllDrillHoles() {
    const drillHolesDataArray = this.getFlattenedDrillHoles()

    for (const drillHoles of drillHolesDataArray) {
      if (!drillHoles.localization || !drillHoles.data.length) continue

      const localizationOffset = new Vector3(
        drillHoles.localization.easting,
        drillHoles.localization.elevation,
        -drillHoles.localization.northing,
      )
      const geoArray = []
      const matrix = new Matrix4()

      for (let i = 0; i < drillHoles.data.length; i++) {
        const drillHole = drillHoles.data[i]
        const collar = drillHole.collar
        const collarGeo = new IcosahedronGeometry(3)

        geoArray.push(collarGeo)

        const position = new Vector3(collar.point.x, collar.point.y, collar.point.z).sub(localizationOffset)
        const vertexCount = collarGeo.attributes.position.count

        const origin = new Float32Array(vertexCount * 3) // 3 floats per vertex
        for (let i = 0; i < vertexCount; i++) {
          origin.set([position.x, position.y, position.z], i * 3)
        }

        collarGeo.setAttribute('origin', new BufferAttribute(origin, 3))

        matrix.setPosition(position)
        collarGeo.applyMatrix4(matrix)
        geoArray.push(collarGeo)
      }

      const geo = BufferGeometryUtils.mergeGeometries(geoArray)
      this.collarMesh = new Mesh(geo, new MeshStandardMaterial({ color: 0xffffff }))
      this.group.add(this.collarMesh)
    }
  }

  private getFlattenedDrillHoles(): DrillHoles[] {
    return Array.from(this.holes.values()).flat()
  }

  private async getDrillHoleMap(drillDataSets: DrillDataSet[]): Promise<Map<number, DrillHoles>> {
    const drillTuples: [number, DrillHoles][] = await Promise.all(
      drillDataSets.map(async dataSet => {
        try {
          //drill data
          const [drillHoles, drillConfig] = await Promise.all([
            $fetch<ApiListView<DrillHoleApiData>>(`/v2/drill/data/${dataSet.data_set_id}/hole_data`, {
              ...useFetchSharedOptions(),
              headers: {
                ...useFetchSharedOptions().headers,
                'X-Deck-Auth-UUID': `${this.loaderConfig.uuid}`,
              },
            }),
            $fetch<DrillHoleApiData[]>(`/v2/drill/data/${dataSet.data_set_id}/configuration`, {
              ...useFetchSharedOptions(),
              headers: {
                ...useFetchSharedOptions().headers,
                'X-Deck-Auth-UUID': `${this.loaderConfig.uuid}`,
              },
            }),
          ])

          return [
            dataSet.data_set_id,
            {
              data: drillHoles.results,
              config: drillConfig,
              localization: dataSet.localization,
            },
          ]
        } catch (e) {
          return [dataSet.data_set_id, { data: [], config: {}, localization: undefined }]
        }
      }),
    )

    return new Map(drillTuples)
  }

  cleanUp() {
    this.group.clear()
    disposeScene(this.group)
  }
}
